import { Box } from '@chakra-ui/react';
import React from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';

import { dashboardRoutePadding } from 'packages/common-frontend/constants';
import { AthenaSchemaProvider } from 'packages/organization/hooks/AthenaSchemaContext';
import { PortfolioBorrowingBaseView } from 'packages/portfolio/ui/PortfolioBorrowingBaseView';
import { PortfolioCovenantsView } from 'packages/portfolio/ui/PortfolioCovenantsView';
import { PortfolioCreditAgreementView } from 'packages/portfolio/ui/PortfolioCreditAgreementView';
import { PortfolioOverviewReportBuilderView } from 'packages/portfolio/ui/PortfolioOverviewReportBuilderView';
import { PortfolioOverviewRiskView } from 'packages/portfolio/ui/PortfolioOverviewRiskView';
import { PortfolioOverviewStratsView } from 'packages/portfolio/ui/PortfolioOverviewStratsView';
import { PortfolioProfitLossView } from 'packages/portfolio/ui/PortfolioProfitLossView';
import { PortfolioReceivableDetailsView } from 'packages/portfolio/ui/PortfolioReceivableDetailsView';
import { PortfolioReceivablesView } from 'packages/portfolio/ui/PortfolioReceivablesView';
import { PortfolioReportBuilderView } from 'packages/portfolio/ui/PortfolioReportBuilderView';
import { PortfolioStratsView } from 'packages/portfolio/ui/PortfolioStratsView';
import { PortfolioSummaryView } from 'packages/portfolio/ui/PortfolioSummaryView';
import { useFeatureFlags } from '~/contexts/FeatureFlagProvider';
import { useUserAuthOrThrow } from '~/contexts/UserAuthProvider';
import { Route as PortfolioOverviewFacilitiesRoute } from '~/core/routes/credit-facilities/_index';
import {
  PortfolioOverviewTemplate,
  PortfolioOverviewTabOptionKeys,
} from '~/packages/portfolio/ui/PortfolioOverviewTemplate';

export const PortfolioRouter = () => {
  const flags = useFeatureFlags();
  const { user } = useUserAuthOrThrow();

  return (
    /**
     * Schema provider does not block children from loading
     * Provider is wrapped around the whole portfolio router to trigger
     * loading of athena schema as soon as portfolio section is loaded to prevent double loading
     *
     * Double loading will not be avoided for direct consumers of the context
     * Context consumers will have to handle loading state themselves using the context provided `AtheneSchemaContextLoadingWrapper`
     */
    <AthenaSchemaProvider organizationId={user.organizationId}>
      {/* Paths relative to "/portfolio/*" */}
      <Routes>
        <Route
          path="/overview/facilities"
          element={
            <Box width="100%" padding={dashboardRoutePadding}>
              <PortfolioOverviewTemplate
                selectedTab={PortfolioOverviewTabOptionKeys.facilities}
              >
                <PortfolioOverviewFacilitiesRoute />
              </PortfolioOverviewTemplate>
            </Box>
          }
        />
        <Route path="/overview/risk" element={<PortfolioOverviewRiskView />} />
        <Route
          path="/overview/strats"
          element={
            flags?.showLenderPortfolioOverviewStrats ? (
              <PortfolioOverviewStratsView />
            ) : (
              <Navigate to="/portfolio/overview/facilities" />
            )
          }
        />
        <Route
          path="/overview/report-builder"
          element={
            flags?.showLenderPortfolioOverviewStrats ? (
              <PortfolioOverviewReportBuilderView />
            ) : (
              <Navigate to="/portfolio/overview/facilities" />
            )
          }
        />

        {/* !TODO @eldarshamukhamedov Many of these routes use query params to store facilityID, should use route params instead */}
        <Route path="/summary" element={<PortfolioSummaryView />} />
        <Route
          path="/covenants"
          element={
            flags?.displayLenderPortfolioMonitoringCovenants ? (
              <PortfolioCovenantsView />
            ) : (
              <Navigate to="/portfolio/overview/facilities" />
            )
          }
        />
        <Route
          path="/borrowing-base"
          element={<PortfolioBorrowingBaseView />}
        />
        <Route path="/profit-loss" element={<PortfolioProfitLossView />} />
        <Route path="/receivables" element={<PortfolioReceivablesView />} />
        <Route
          path="/receivables/:finleyReceivableId"
          element={
            <PortfolioReceivableDetailsView
              organizationId={user.organizationId}
            />
          }
        />
        <Route
          path="/credit-agreement"
          element={<PortfolioCreditAgreementView />}
        />
        <Route
          path="/strats"
          element={
            flags?.showLenderFacilitiesStrats &&
            flags?.showLenderFacilitiesStrats !== 'hide' ? (
              <PortfolioStratsView />
            ) : (
              <Navigate to="/portfolio/overview/facilities" />
            )
          }
        />
        <Route
          path="/report-builder"
          element={
            flags?.showExploReportBuilder ? (
              <PortfolioReportBuilderView />
            ) : (
              <Navigate to="/portfolio/overview/facilities" />
            )
          }
        />
        <Route
          path="/*"
          element={<Navigate to="/portfolio/overview/facilities" />}
        />
      </Routes>
    </AthenaSchemaProvider>
  );
};
